import { Bubble, Projectile, W, R, D } from './types';
import { cellCenter } from './grid';

export interface FlightStep {
  proj: Projectile;
  landed: boolean;
  bounced: boolean;   // hit a side wall during this advance
}

function collides(x: number, y: number, bubbles: Map<string, Bubble>, dropCount: number): boolean {
  const thresh = (D - 1) * (D - 1);
  for (const b of bubbles.values()) {
    const c = cellCenter(b.row, b.col, dropCount);
    const dx = c.x - x, dy = c.y - y;
    if (dx * dx + dy * dy < thresh) return true;
  }
  return false;
}

// Advance the projectile by dt seconds in sub-steps (≤ R/2 each) so it cannot
// tunnel through walls or the grid at high speed. Stops on ceiling/grid contact.
export function advanceProjectile(
  p: Projectile, dt: number, bubbles: Map<string, Bubble>, dropCount: number,
): FlightStep {
  const speed = Math.hypot(p.vx, p.vy) || 1;
  const subdt = Math.min(dt, (R * 0.5) / speed);
  let { x, y, vx, vy } = p;
  let remaining = dt;
  let bounced = false;

  while (remaining > 0) {
    const step = Math.min(subdt, remaining);
    remaining -= step;
    x += vx * step;
    y += vy * step;

    if (x <= R) { x = R; vx = -vx; bounced = true; }
    else if (x >= W - R) { x = W - R; vx = -vx; bounced = true; }

    if (y <= R) {
      return { proj: { x, y: R, vx, vy, color: p.color }, landed: true, bounced };
    }
    if (collides(x, y, bubbles, dropCount)) {
      return { proj: { x, y, vx, vy, color: p.color }, landed: true, bounced };
    }
  }
  return { proj: { x, y, vx, vy, color: p.color }, landed: false, bounced };
}

// Aim-guide polyline: ray from the launcher with up to one wall bounce, stopping
// at the ceiling, a grid bubble, or after the bounce. Returns points to draw.
// Cached: recomputed only when angle/bubbles(ref)/dropCount change, since it's
// called every RAF frame while aiming but the inputs rarely change (bubbles is
// an immutable Map, replaced by reference on every shot/ceiling-drop).
let aimCache: {
  startX: number; startY: number; angle: number;
  bubbles: Map<string, Bubble>; dropCount: number;
  pts: { x: number; y: number }[];
} | null = null;

export function aimPath(
  startX: number, startY: number, angle: number,
  bubbles: Map<string, Bubble>, dropCount: number,
): { x: number; y: number }[] {
  if (
    aimCache &&
    aimCache.startX === startX && aimCache.startY === startY &&
    aimCache.angle === angle && aimCache.bubbles === bubbles &&
    aimCache.dropCount === dropCount
  ) {
    return aimCache.pts;
  }

  const pts = [{ x: startX, y: startY }];
  let x = startX, y = startY;
  let vx = Math.cos(angle), vy = -Math.sin(angle);
  let bounces = 0;
  const stepLen = 4;
  for (let i = 0; i < 1000; i++) {
    x += vx * stepLen;
    y += vy * stepLen;
    if (x <= R) { x = R; vx = -vx; pts.push({ x, y }); if (++bounces > 1) break; continue; }
    if (x >= W - R) { x = W - R; vx = -vx; pts.push({ x, y }); if (++bounces > 1) break; continue; }
    if (y <= R) { pts.push({ x, y: R }); break; }
    if (collides(x, y, bubbles, dropCount)) { pts.push({ x, y }); break; }
  }
  aimCache = { startX, startY, angle, bubbles, dropCount, pts };
  return pts;
}
